import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Appointment } from '../utils/appointmentManager';
import { stripePaymentService } from '../services/stripePaymentService';
import { braintreeService } from '../services/braintreeService';
import PaymentMethodBrandIcon from './PaymentMethodBrandIcon';

interface AppointmentPaymentModalProps {
  visible: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onPaymentSuccess: (appointment: Appointment) => void;
}

const AppointmentPaymentModal: React.FC<AppointmentPaymentModalProps> = ({
  visible,
  onClose,
  appointment,
  onPaymentSuccess,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const showAlertOk = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      (globalThis as unknown as { alert?: (m: string) => void }).alert?.(
        `${title}\n\n${message}`
      );
    } else {
      Alert.alert(title, message, [{ text: 'OK' }]);
    }
  };

  // Check if business owner has connected payment provider
  const checkPaymentProvider = async (): Promise<boolean> => {
    try {
      // In production, this would check your backend API
      // For now, return false to trigger payment provider setup
      return false;
    } catch (error) {
      console.error('Error checking payment provider:', error);
      return false;
    }
  };

  // Open payment provider setup screen
  const openPaymentProviderSetup = () => {
    // This would navigate to PaymentProviderScreen
    // For now, show an alert
    Alert.alert(
      'Payment Provider Setup',
      'Payment provider setup (Stripe or Braintree) will be available in the next update. For now, you can use other payment methods.',
      [{ text: 'OK' }]
    );
  };

  const paymentMethods = [
    {
      id: 'paypal',
      name: 'PayPal',
      description: 'Pay the business on PayPal (direct — not processed by BookAPT)',
      comingSoon: false,
    },
    { id: 'venmo', name: 'Venmo', description: 'Send Venmo payment request', comingSoon: false },
    { id: 'apple_pay', name: 'Apple Pay', description: 'Process Apple Pay payment', comingSoon: true },
    { id: 'google_pay', name: 'Google Pay', description: 'Process Google Pay payment', comingSoon: true },
    { id: 'qr_code', name: 'QR Code Payment', description: 'Generate QR code for customer to scan', comingSoon: true },
    { id: 'sms_request', name: 'SMS Payment Request', description: 'Send payment link via SMS', comingSoon: false },
    { id: 'app_chat', name: 'App Chat Request', description: 'Send payment request through app chat', comingSoon: false },
    { id: 'cash', name: 'Cash Payment', description: 'Mark as cash payment received', comingSoon: false },
  ];

  const handlePaymentMethodSelect = async (methodId: string) => {
    if (!appointment) return;

    setSelectedMethod(methodId);
    setLoading(true);

    try {
  // Show "Coming Soon" for credit card and mobile payments only
  if (['credit_card', 'apple_pay', 'google_pay'].includes(methodId)) {
    showAlertOk(
      'Payment Processing Coming Soon!',
      "We're working on integrating credit card and mobile payment processing. This feature will be available in the next update. For now, you can use Venmo, PayPal, SMS requests, or cash payments."
    );
    setLoading(false);
    setSelectedMethod(null);
    return;
  }

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      switch (methodId) {
        case 'credit_card':
          try {
            const paymentRequest = {
              amount: appointment.estimatedCost || 0,
              currency: 'USD',
              serviceName: appointment.serviceType || 'Service',
              customerId: appointment.customerName,
              businessId: 'business_1',
            };

            // For demo purposes, use test card details
            const cardDetails = {
              number: '4242424242424242',
              expMonth: 12,
              expYear: 2025,
              cvc: '123',
              name: appointment.customerName,
              postalCode: '76010',
            };

            const result = await stripePaymentService.processCreditCard(paymentRequest, cardDetails);
            
            if (result.success) {
              const updatedAppointment = {
                ...appointment,
                paymentData: {
                  amount: appointment.estimatedCost || 0,
                  paymentMethod: 'credit_card',
                  status: 'completed',
                  transactionId: result.transactionId,
                  processedAt: new Date().toISOString(),
                },
                status: 'paid' as const,
              };
              onPaymentSuccess(updatedAppointment);
              Alert.alert('Success', 'Credit card payment processed successfully!');
            } else {
              Alert.alert('Payment Failed', result.error || 'Payment could not be processed');
            }
          } catch (error) {
            console.error('Credit card payment error:', error);
            Alert.alert('Error', 'Payment processing failed. Please try again.');
          }
          break;

        case 'venmo':
          try {
            const paymentRequest = {
              amount: appointment.estimatedCost || 0,
              serviceName: appointment.serviceType || 'Service',
              customerId: appointment.customerName,
              businessId: 'business_1',
              description: `${appointment.serviceType} - ${appointment.customerName}`,
              // Add customer contact info for SMS/email
              customerPhone: appointment.phone,
              customerEmail: appointment.email,
            };

            const result = await braintreeService.processVenmoPayment(paymentRequest);
            
            if (result.success) {
              const updatedAppointment = {
                ...appointment,
                paymentData: {
                  amount: appointment.estimatedCost || 0,
                  paymentMethod: 'venmo',
                  status: 'completed',
                  transactionId: result.transactionId,
                  processedAt: new Date().toISOString(),
                },
                status: 'paid' as const,
              };
              onPaymentSuccess(updatedAppointment);
              Alert.alert('Success', 'Venmo payment processed successfully!');
            } else {
              Alert.alert('Payment Failed', result.error || 'Venmo payment could not be processed');
            }
          } catch (error) {
            console.error('Venmo payment error:', error);
            Alert.alert('Error', 'Venmo payment processing failed. Please try again.');
          }
          break;

        case 'paypal':
          try {
            const { openPayPalDirectPayment } = await import(
              '../services/paypalDirectPaymentService'
            );
            const businessId = appointment.businessOwnerId || '';
            const amount = appointment.estimatedCost || 0;
            const result = await openPayPalDirectPayment({
              businessId,
              amountUsd: amount > 0 ? amount : undefined,
            });
            if (result.opened) {
              showAlertOk(
                'Pay on PayPal',
                'Complete payment in PayPal (you pay the business directly). The business will confirm payment in BookAPT.'
              );
            } else {
              showAlertOk('PayPal', result.error || 'PayPal is not set up for this business.');
            }
          } catch (error) {
            console.error('PayPal direct payment error:', error);
            showAlertOk('Error', 'Could not open PayPal. Please try again.');
          }
          break;

        case 'apple_pay':
          try {
            const paymentRequest = {
              amount: appointment.estimatedCost || 0,
              currency: 'USD',
              serviceName: appointment.serviceType || 'Service',
              customerId: appointment.customerName,
              businessId: 'business_1',
            };

            const result = await stripePaymentService.processApplePay(paymentRequest);
            
            if (result.success) {
              const updatedAppointment = {
                ...appointment,
                paymentData: {
                  amount: appointment.estimatedCost || 0,
                  paymentMethod: 'apple_pay',
                  status: 'completed',
                  transactionId: result.transactionId,
                  processedAt: new Date().toISOString(),
                },
                status: 'paid' as const,
              };
              onPaymentSuccess(updatedAppointment);
              Alert.alert('Success', 'Apple Pay payment processed successfully!');
            } else {
              Alert.alert('Payment Failed', result.error || 'Apple Pay payment could not be processed');
            }
          } catch (error) {
            console.error('Apple Pay payment error:', error);
            Alert.alert('Error', 'Apple Pay payment processing failed. Please try again.');
          }
          break;

        case 'google_pay':
          try {
            const paymentRequest = {
              amount: appointment.estimatedCost || 0,
              currency: 'USD',
              serviceName: appointment.serviceType || 'Service',
              customerId: appointment.customerName,
              businessId: 'business_1',
            };

            const result = await stripePaymentService.processGooglePay(paymentRequest);
            
            if (result.success) {
              const updatedAppointment = {
                ...appointment,
                paymentData: {
                  amount: appointment.estimatedCost || 0,
                  paymentMethod: 'google_pay',
                  status: 'completed',
                  transactionId: result.transactionId,
                  processedAt: new Date().toISOString(),
                },
                status: 'paid' as const,
              };
              onPaymentSuccess(updatedAppointment);
              Alert.alert('Success', 'Google Pay payment processed successfully!');
            } else {
              Alert.alert('Payment Failed', result.error || 'Google Pay payment could not be processed');
            }
          } catch (error) {
            console.error('Google Pay payment error:', error);
            Alert.alert('Error', 'Google Pay payment processing failed. Please try again.');
          }
          break;

        case 'qr_code':
          Alert.alert(
            'QR Code Generated',
            'QR code has been generated for the customer to scan. Payment will be processed when scanned.',
            [
              {
                text: 'OK',
                onPress: () => {
                  // Update appointment with payment info
                  const updatedAppointment = {
                    ...appointment,
                    paymentData: {
                      amount: appointment.estimatedCost || 0,
                      paymentMethod: 'qr_code',
                      status: 'pending',
                      generatedAt: new Date().toISOString(),
                    },
                    status: 'confirmed' as const,
                  };
                  onPaymentSuccess(updatedAppointment);
                },
              },
            ]
          );
          break;

        case 'sms_request':
          Alert.alert(
            'SMS Sent',
            'Payment request SMS has been sent to the customer. Payment will be processed when they complete it.',
            [
              {
                text: 'OK',
                onPress: () => {
                  const updatedAppointment = {
                    ...appointment,
                    paymentData: {
                      amount: appointment.estimatedCost || 0,
                      paymentMethod: 'sms_request',
                      status: 'pending',
                      sentAt: new Date().toISOString(),
                    },
                    status: 'confirmed' as const,
                  };
                  onPaymentSuccess(updatedAppointment);
                },
              },
            ]
          );
          break;

        case 'app_chat':
          Alert.alert(
            'Chat Request Sent',
            'Payment request has been sent through the app chat. Customer will receive a notification.',
            [
              {
                text: 'OK',
                onPress: () => {
                  const updatedAppointment = {
                    ...appointment,
                    paymentData: {
                      amount: appointment.estimatedCost || 0,
                      paymentMethod: 'app_chat',
                      status: 'pending',
                      sentAt: new Date().toISOString(),
                    },
                    status: 'confirmed' as const,
                  };
                  onPaymentSuccess(updatedAppointment);
                },
              },
            ]
          );
          break;

        case 'cash':
          Alert.alert(
            'Cash Payment Recorded',
            'Cash payment has been recorded for this appointment.',
            [
              {
                text: 'OK',
                onPress: () => {
                  const updatedAppointment = {
                    ...appointment,
                    paymentData: {
                      amount: appointment.estimatedCost || 0,
                      paymentMethod: 'cash',
                      status: 'completed',
                      receivedAt: new Date().toISOString(),
                    },
                    status: 'completed' as const,
                  };
                  onPaymentSuccess(updatedAppointment);
                },
              },
            ]
          );
          break;

      }
    } catch (error) {
      console.error('Error processing payment:', error);
      Alert.alert('Error', 'Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
      setSelectedMethod(null);
    }
  };

  if (!appointment) return null;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Process Payment</Text>
          <View style={styles.placeholder} />
        </View>

        <ScrollView style={styles.content}>
          {/* Appointment Info */}
          <View style={styles.appointmentCard}>
            <Text style={styles.appointmentTitle}>Appointment Details</Text>
            <Text style={styles.appointmentInfo}>Customer: {appointment.customerName}</Text>
            <Text style={styles.appointmentInfo}>Service: {appointment.serviceType}</Text>
            <Text style={styles.appointmentInfo}>Date: {appointment.appointmentDate}</Text>
            <Text style={styles.appointmentInfo}>Time: {appointment.appointmentTime}</Text>
            <Text style={styles.appointmentAmount}>
              Amount: ${appointment.estimatedCost || 0}
            </Text>
          </View>

          {/* Payment Methods */}
          <View style={styles.paymentMethodsContainer}>
            <Text style={styles.sectionTitle}>Choose Payment Method</Text>
            {paymentMethods.map((method) => (
              <TouchableOpacity
                key={method.id}
                style={[
                  styles.paymentMethodCard,
                  selectedMethod === method.id && styles.selectedMethodCard,
                  method.comingSoon && styles.comingSoonCard,
                ]}
                onPress={() => handlePaymentMethodSelect(method.id)}
                disabled={loading}
              >
                <View style={styles.methodIcon}>
                  <PaymentMethodBrandIcon methodId={method.id} size={32} />
                </View>
                <View style={styles.methodInfo}>
                  <View style={styles.methodNameContainer}>
                    <Text style={styles.methodName}>{method.name}</Text>
                    {method.comingSoon && (
                      <View style={styles.comingSoonBadge}>
                        <Text style={styles.comingSoonText}>Coming Soon</Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.methodDescription}>{method.description}</Text>
                </View>
                {loading && selectedMethod === method.id && (
                  <ActivityIndicator size="small" color="#007AFF" />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: 'bold',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  placeholder: {
    width: 30,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  appointmentCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  appointmentTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  appointmentInfo: {
    fontSize: 16,
    color: '#666',
    marginBottom: 8,
  },
  appointmentAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007AFF',
    marginTop: 10,
  },
  paymentMethodsContainer: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  paymentMethodCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedMethodCard: {
    borderWidth: 2,
    borderColor: '#007AFF',
    backgroundColor: '#f0f8ff',
  },
  methodIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 15,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#e5e5ea',
  },
  methodInfo: {
    flex: 1,
  },
  methodName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  methodDescription: {
    fontSize: 14,
    color: '#666',
  },
  comingSoonCard: {
    opacity: 0.7,
  },
  methodNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  comingSoonBadge: {
    backgroundColor: '#ffc107',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    marginLeft: 8,
  },
  comingSoonText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#856404',
  },
});

export default AppointmentPaymentModal;
